import { useRef } from 'react';
import type {
  PhieuXuatDieuChuyenAccountData,
  PhieuXuatDieuChuyenCellValue,
  PhieuXuatDieuChuyenChiTiet,
  PhieuXuatDieuChuyenDetailSetter,
  PhieuXuatDieuChuyenDetailUpdate,
  PhieuXuatDieuChuyenNhapXuatData,
  PhieuXuatDieuChuyenSelectedRowSetter,
  PhieuXuatDieuChuyenVatTuData
} from '@/types/schemas';
import { useAuth } from '@/contexts/auth-context';
import { QUERY_KEYS } from '@/constants';
import api from '@/lib/api';

type ReasonAutoFillSetters = {
  setRows: PhieuXuatDieuChuyenDetailSetter;
  setSelectedRow: PhieuXuatDieuChuyenSelectedRowSetter;
  onChange?: (rows: PhieuXuatDieuChuyenChiTiet[]) => void;
  selectedRowUuid: string | null;
};

export interface ReasonAutoFillResult {
  latestReasonCodeRef: React.MutableRefObject<Record<string, string>>;
  applyReasonToRow: (
    rowUuid: string,
    reasonRecord: PhieuXuatDieuChuyenNhapXuatData | null,
    setters: ReasonAutoFillSetters
  ) => void;
  handleFieldUpdates: (params: {
    row: PhieuXuatDieuChuyenChiTiet;
    field: string;
    value: PhieuXuatDieuChuyenCellValue;
    setters: ReasonAutoFillSetters;
  }) => PhieuXuatDieuChuyenDetailUpdate;
}

export function useReasonAutoFill(): ReasonAutoFillResult {
  const { entity } = useAuth();
  const latestReasonCodeRef = useRef<Record<string, string>>({});

  const applyReasonToRow = (
    rowUuid: string,
    reasonRecord: PhieuXuatDieuChuyenNhapXuatData | null,
    { setRows, setSelectedRow, onChange, selectedRowUuid }: ReasonAutoFillSetters
  ) => {
    setRows((prevRows: PhieuXuatDieuChuyenChiTiet[]) => {
      const rowIndex = prevRows.findIndex(row => row.uuid === rowUuid);
      if (rowIndex === -1) return prevRows;

      const currentRow = prevRows[rowIndex];
      const updatedRow = {
        ...currentRow,
        ma_nx_data: reasonRecord || null,
        ...(reasonRecord?.uuid ? { ma_nx: reasonRecord.uuid } : { ma_nx: null })
      };

      const updatedRows = [...prevRows];
      updatedRows[rowIndex] = updatedRow;

      onChange?.(updatedRows);

      if (rowUuid === selectedRowUuid) {
        setSelectedRow(updatedRow);
      }

      return updatedRows;
    });
  };

  const fetchReasonByAccountCode = async (
    rowUuid: string,
    accountCode: string | null | undefined,
    setters: ReasonAutoFillSetters
  ) => {
    if (!rowUuid || !accountCode || !entity?.slug) return;

    try {
      const response = await api.get(`/entities/${entity.slug}/erp/${QUERY_KEYS.LY_DO_NHAP_XUAT}/`, {
        params: { search: accountCode, page_size: 1 }
      });

      const list = response.data?.results ?? response.data ?? [];
      const reasonList = Array.isArray(list) ? (list as PhieuXuatDieuChuyenNhapXuatData[]) : [];
      const matched = reasonList.find(item => item.ma_nx === accountCode) || reasonList[0] || null;

      if (latestReasonCodeRef.current[rowUuid] !== accountCode) {
        return;
      }

      applyReasonToRow(rowUuid, matched, setters);
    } catch (error) {
      console.warn('Failed to auto-select ma_nx_data', error);
    }
  };

  const handleFieldUpdates = ({
    row,
    field,
    value,
    setters
  }: {
    row: PhieuXuatDieuChuyenChiTiet;
    field: string;
    value: PhieuXuatDieuChuyenCellValue;
    setters: ReasonAutoFillSetters;
  }) => {
    const updates: PhieuXuatDieuChuyenDetailUpdate = {};

    if (field === 'ma_vt_data') {
      const tkFromItem = (value as PhieuXuatDieuChuyenVatTuData | null)?.tk_khcp_data || null;
      updates.tk_vt_data = tkFromItem || null;
      updates.tk_du_data = tkFromItem || null;

      const accountCode = tkFromItem?.code || null;
      const rowUuid = row.uuid || '';
      latestReasonCodeRef.current[rowUuid] = accountCode || '';
      fetchReasonByAccountCode(rowUuid, accountCode, setters);
    }

    if (field === 'ma_nx_data') {
      updates.tk_du_data = (value as PhieuXuatDieuChuyenNhapXuatData | null)
        ?.tk_data as PhieuXuatDieuChuyenAccountData | null;
    }

    return updates;
  };

  return {
    latestReasonCodeRef,
    applyReasonToRow,
    handleFieldUpdates
  };
}
